import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import SuperClusterAlgorithm from '../../utils/superClusterAlgorithm';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuth, selectIsMaster } from '../../features/auth/authSlice';
import { useGetPermissionCheckQuery } from '../../features/master/masterApiSlice';
import NotificationDialog from '../HeaderContainer/Popup/NotificationDialog';
import { useTranslation } from 'react-i18next';

const Map = ({ lang, trCategoriesArr, jobs, mapZoom, setBounds, bounds, center, isMapApiLoaded }) => {
    const { t } = useTranslation();
    const [curBounds, setCurBounds] = useState(null);
    const [prevCenter, setPrevCenter] = useState(null);
    const isAuth = useSelector(selectIsAuth);
    const isMaster = useSelector(selectIsMaster);
    const [mapContainer, setMapContainer] = useState(null);
    const [markerCluster, setMarkerCluster] = useState(null);
    const ref = useRef(null);
    const boundsTimerRef = useRef(null);
    const prevMarkersRef = useRef([]);
    const navigate = useNavigate();
    const { data:permission } = useGetPermissionCheckQuery();
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    
    useEffect(() => {
        if (ref.current && !mapContainer) { 
            const map = new window.google.maps.Map(ref.current, {
                center: center,
                zoom: mapZoom,
                maxZoom: 18,
                gestureHandling: 'cooperative',
                disableDefaultUI: true,
                fullscreenControl: true,
                fullscreenControlOptions: {
                    position: window.google.maps.ControlPosition.TOP_RIGHT
                },
                zoomControl: true,
                zoomControlOptions: {
                    position: window.google.maps.ControlPosition.RIGHT_CENTER
                },
                keyboardShortcuts: true,
            });
            setMapContainer(map);

            setPrevCenter(center);

            //bounds_changed
            const idleListener = window.google.maps.event.addListener(map, 'idle', function() {
                const bounds = map.getBounds();
                const ne = bounds.getNorthEast(); // Coords of the northeast corner
                const sw = bounds.getSouthWest(); // Coords of the southwest corner

                clearTimeout(boundsTimerRef.current);
                boundsTimerRef.current = setTimeout(() => {
                    setBounds({
                        lats: {
                            startLat: sw.lat(),
                            endLat: ne.lat()
                        },
                        lngs: {
                            startLng: sw.lng(),
                            endLng: ne.lng()
                        }
                    });
                }, 400);
                setCurBounds(bounds);
            });

            return () => {
                clearTimeout(boundsTimerRef.current);
                idleListener.remove();
            };
        }
    }, [ref?.current]);

    useEffect(() => { 
        if (mapContainer && jobs) {
            /**
             * оновлюємо кластер та маркери 
             * без центрування та зумування
             */
            //debugger;
            if (bounds && curBounds) {  
                if (!(prevCenter.lat == center.lat && prevCenter.lng == center.lng)) {
                    if (!curBounds.contains(center)) {
                        console.log('Center was changed: ', center);
                        mapContainer?.setCenter(center);
                        mapContainer?.setZoom(mapZoom);

                        setPrevCenter(center);
                    }   
                } 
            }           

            if (markerCluster && prevMarkersRef.current.length > 0) {
                clearAllMarkersFromCluster();
                clearMarkers(prevMarkersRef.current);
            }

            //addMarkers(mapContainer, locations);
            addMarkers(mapContainer, jobs);
        }
    }, [center, mapContainer, bounds, jobs]);
    /*
        *при ініціалізації карти, 
        *встановлення центру з селесту, 
        *або після взаємодії з картою(zoom, рух карти) 

    оновлюємо кластер та маркери*/

    useEffect(() => {
        if (mapContainer && jobs) {
            if (markerCluster && prevMarkersRef.current.length > 0) {
                clearAllMarkersFromCluster();
                clearMarkers(prevMarkersRef.current);
            }

            addMarkers(mapContainer, jobs);
        }
    }, [lang]);

    function openJob(jobId, jobCategory) {
        if (isAuth && isMaster) {
            if (permission) {
                navigate(`/master-office/job/${encodeURIComponent(jobCategory.replace('/', '-'))}/${jobId}`,
                    { state: { id: jobId, name: 'ApplyForAJob', page: 'ApplyForAJob', isApply: true } }
                );
            } else {
                setIsNotificationOpen(true);
            }
        }
    }

    function createMarker(jobsAtLocation, position, map, infoWindow) {
        /*and then, if you want to change the marker dynamically (like on mouseover), you can, for example:
    
            oMarker.setIcon({
                path: google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: "#0F0",
                fillOpacity: 0.8,
                strokeWeight: 1
            }) */
        const marker = new window.google.maps.Marker({ 
            position: position,
            icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 9,
                fillColor: "#0101ff",
                strokeColor: '#0101ff',
                //fillOpacity: 0.4,
                fillOpacity: 1,
                strokeWeight: 9,
                strokeOpacity: 0.15
            },
        });

        marker.addListener('click', () => {
            infoWindow.setPosition(position);
            const popup = document.createElement('div');
            popup.style.maxHeight = '240px';
            popup.style.overflowY = 'auto';
            popup.style.minWidth = '260px';

            jobsAtLocation.forEach(job => {
                const item = document.createElement('button');
                item.type = 'button';
                item.className = 'job-listing-description';
                item.style.display = 'block';
                item.style.width = '100%';
                item.style.padding = '10px 6px';
                item.style.border = '0';
                item.style.borderBottom = '1px solid #eee';
                item.style.background = '#fff';
                item.style.textAlign = 'left';
                item.style.cursor = 'pointer';

                const title = document.createElement('h3');
                title.className = 'job-listing-title';
                title.style.fontSize = '16px';
                title.style.lineHeight = '24px';
                title.style.margin = '0';
                title.textContent = job.localizedCategory;

                const company = document.createElement('h4');
                company.className = 'job-listing-company';
                company.style.fontSize = '14px';
                company.style.margin = '2px 0 0';
                company.textContent = job.clientName;

                item.append(title, company);
                item.addEventListener('click', () => openJob(job.id, job.localizedCategory));
                popup.appendChild(item);
            });

            infoWindow.setContent(popup);
            infoWindow.open({ map });
        });

        return marker;
    }

    function clearMarkers(markers) {
        for (let m of markers) {
            m.setMap(null);
        }
    }

    function clearAllMarkersFromCluster() {
        markerCluster.clearMarkers();
    }

    function addMarkers(map, jobsArr) {
        const infoWindow = new window.google.maps.InfoWindow({ 
            maxWidth: '320px', 
            maxHeight: '91px',
            minWidth: '300px',
        });
        
        const groupedJobs = new window.Map();
        jobsArr?.forEach(job => {
            const lat = Number(job.lat);
            const lng = Number(job.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

            const index = trCategoriesArr?.input?.indexOf(job.category);
            const localizedCategory = trCategoriesArr?.translated?.[index]?.[lang] || job.category;
            const key = `${lat.toFixed(6)}:${lng.toFixed(6)}`;
            const group = groupedJobs.get(key) || { position: { lat, lng }, jobs: [] };
            group.jobs.push({ ...job, localizedCategory });
            groupedJobs.set(key, group);
        });

        const markers = Array.from(groupedJobs.values()).map(group =>
            createMarker(group.jobs, group.position, map, infoWindow)
        );
        prevMarkersRef.current = markers;
    
        if (markerCluster) {
            markerCluster.addMarkers(markers);
        } else {
            setMarkerCluster(new MarkerClusterer ({
                markers,
                map,
                algorithm: new SuperClusterAlgorithm({ radius: 70 })
            }));
        }
        
    }

    return <>
        {isMapApiLoaded
            ? <div id="map" ref={ref}></div>
            : <div>Loading...</div>}
        <NotificationDialog open={isNotificationOpen} onClose={() => setIsNotificationOpen(false)}>
            {t('MasterWithoutPermission')}
        </NotificationDialog>
    </>;
}


export default Map;
